import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type FullCalendar from '@fullcalendar/react';
import type { CalendarEvent } from '@/components/types/typeCalendar';

// Debe cubrir la animación .cita-destacada de index.css (3.6s) más un margen.
const DURACION_RESALTADO_MS = 3900;
const INTENTOS = 25;
const ESPERA_ENTRE_INTENTOS_MS = 250;
// Margen amplio: las citas pueden tardar en llegar y el calendario se
// remonta al terminar la carga.
const ESPERA_MAXIMA_MS = 20000;
// Margen sin cambios en `events` para dar la carga por terminada.
const ESPERA_CARGA_MS = 400;

// TEMPORAL: traza para localizar dónde se corta el salto a la cita.
// Quitar cuando funcione.
const traza = (...args: unknown[]) => console.log('[citaDestacada]', ...args);

/**
 * Centra el evento dentro del contenedor con scroll del grid.
 *
 * scrollIntoView no sirve: los eventos de FullCalendar viven en un harness
 * posicionado en absoluto y el navegador termina desplazando un ancestro que
 * no es .fc-scroller. El scrollTop se asigna directamente porque scrollTo con
 * behavior smooth se queda a medias si el grid aún está recolocándose.
 */
function centrarEnScroller(
  el: HTMLElement,
  hora: string,
  calendarRef: React.MutableRefObject<FullCalendar | null>
) {
  const scroller = el.closest<HTMLElement>('.fc-scroller');
  traza('5. scroller encontrado:', !!scroller, scroller?.className);

  if (!scroller) {
    calendarRef.current?.getApi().scrollToTime(hora);
    return;
  }

  const evento = el.getBoundingClientRect();
  const caja = scroller.getBoundingClientRect();
  const destino =
    scroller.scrollTop +
    (evento.top - caja.top) -
    (caja.height - evento.height) / 2;

  const antes = scroller.scrollTop;
  scroller.scrollTop = Math.max(0, destino);
  traza('6. scrollTop antes/destino/despues:', antes, Math.round(destino), scroller.scrollTop);

  // Si el contenedor no se ha movido es que no era el que scrollea.
  if (scroller.scrollTop === antes && Math.abs(destino - antes) > 4) {
    calendarRef.current?.getApi().scrollToTime(hora);
  }
}

/**
 * Lleva el calendario a la cita que venía en la notificación.
 *
 * La campana navega a la ruta del calendario con ?cita=<id_cita>&fecha=<YYYY-MM-DD>.
 * La fecha es imprescindible: las citas se piden por rango (fechaInicio/fechaFin),
 * así que hay que situarse en el día ANTES de que se pidan los datos.
 *
 * El evento se localiza por el data-id-cita que ponen los calendarios en
 * eventDidMount, y la clase del resaltado se aplica sobre ese elemento. Se
 * hizo así porque eventClassNames solo se reevalúa cuando FullCalendar
 * repinta los eventos, no cuando cambia un estado de React: el resaltado
 * entraba de forma intermitente y el centrado se quedaba sin referencia.
 */
export function useCitaDestacada({
  calendarRef,
  events,
  calendarView,
  setCalendarView,
  forzarVistaDia = false,
}: {
  calendarRef: React.MutableRefObject<FullCalendar | null>;
  events: CalendarEvent[];
  calendarView: string;
  setCalendarView: (vista: any) => void;
  forzarVistaDia?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const citaParam = searchParams.get('cita');
  const fechaParam = searchParams.get('fecha');

  // Guardan PARA QUÉ cita se cumplieron, no un simple booleano: al pulsar
  // una notificación estando ya en el calendario, React Router solo cambia
  // los search params y el componente no se desmonta, así que unos refs
  // booleanos se quedaban a true de la vez anterior y la segunda
  // notificación no hacía nada.
  const vistaForzadaPara = useRef<string | null>(null);
  const resueltoPara = useRef<string | null>(null);

  // Lo que hay que centrar, una vez localizado entre los eventos cargados.
  const [objetivo, setObjetivo] = useState<{ id: string; hora: string } | null>(
    null
  );

  // Solo en móvil: en una pantalla estrecha la vista semanal no deja leer la
  // cita. En escritorio se respeta la vista que tuviera el usuario.
  useEffect(() => {
    if (!fechaParam || !forzarVistaDia) return;
    if (vistaForzadaPara.current === fechaParam) return;
    vistaForzadaPara.current = fechaParam;
    setCalendarView('timeGridDay');
  }, [fechaParam, forzarVistaDia, setCalendarView]);

  // No basta con un gotoDate y parar: los calendarios devuelven un spinner
  // mientras cargan (if (loading || cargandoDatos) return ...), así que
  // FullCalendar se monta después y arranca de nuevo en la fecha de hoy,
  // descartando el salto. Se comprueba que la fecha siga dentro del rango
  // visible y se reaplica si se ha perdido, hasta confirmarlo dos veces
  // seguidas para no pelearse con el usuario si navega él.
  //
  // Depende de calendarView porque al cambiar de vista se remonta
  // (key={calendarView}) y vuelve a empezar en hoy.
  useEffect(() => {
    if (!fechaParam) return;

    const objetivoMs = new Date(`${fechaParam}T00:00:00`).getTime();
    let intentos = 0;
    let confirmaciones = 0;

    const id = setInterval(() => {
      const api = calendarRef.current?.getApi();

      if (api) {
        const { activeStart, activeEnd } = api.view;
        const dentro =
          objetivoMs >= activeStart.getTime() &&
          objetivoMs < activeEnd.getTime();

        if (dentro) {
          if (++confirmaciones >= 2) {
            clearInterval(id);
            return;
          }
        } else {
          confirmaciones = 0;
          traza('0. reaplicando gotoDate', fechaParam);
          api.gotoDate(fechaParam);
        }
      }

      if (++intentos >= INTENTOS) clearInterval(id);
    }, ESPERA_ENTRE_INTENTOS_MS);

    return () => clearInterval(id);
  }, [fechaParam, calendarView, calendarRef]);

  // Localiza la cita y anota el objetivo, pero solo cuando la carga se ha
  // asentado: las citas llegan en varias tandas y FullCalendar repinta con
  // cada una, lo que borraba la clase a media animación y descuadraba el
  // centrado. Cada cambio de `events` reinicia la espera.
  useEffect(() => {
    traza('1. params:', { citaParam, fechaParam, eventos: events?.length ?? 0 });
    if (!citaParam || resueltoPara.current === citaParam || !events?.length)
      return;

    const id = setTimeout(() => {
      const bloques = events
        .filter((e) => e.extendedProps?.id_cita === citaParam)
        .sort((a, b) => a.start.localeCompare(b.start));

      traza('2. bloques de esa cita:', bloques.length);
      if (!bloques.length) return;
      resueltoPara.current = citaParam;

      setObjetivo({
        id: citaParam,
        hora: new Date(bloques[0].start).toTimeString().slice(0, 8),
      });

      // Se limpian los params para que al navegar por el calendario o
      // recargar no se vuelva a saltar al mismo día.
      setSearchParams(
        (prev) => {
          const resto = new URLSearchParams(prev);
          resto.delete('cita');
          resto.delete('fecha');
          return resto;
        },
        { replace: true }
      );
    }, ESPERA_CARGA_MS);

    return () => clearTimeout(id);
  }, [citaParam, fechaParam, events, setSearchParams]);

  // En su propio efecto y con `objetivo` como única dependencia. Antes esto
  // vivía junto a la búsqueda de la cita, y como allí estaban `events` y
  // `searchParams` en las dependencias, el cleanup mataba el observador a los
  // pocos milisegundos de crearlo.
  //
  // El observador se mantiene vivo mientras dura el resaltado, no solo hasta
  // encontrar el evento: FullCalendar destruye y recrea los elementos en cada
  // repintado, así que la clase se perdía a media animación y quedaba un nodo
  // suelto con el efecto a medias.
  useEffect(() => {
    if (!objetivo) return;

    const selector = `.fc-event[data-id-cita="${objetivo.id}"]`;
    traza('3. esperando', selector, '| marcados en DOM:',
      document.querySelectorAll('.fc-event[data-id-cita]').length);

    // Restos de una notificación anterior.
    document
      .querySelectorAll('.cita-destacada')
      .forEach((el) => el.classList.remove('cita-destacada'));

    let encontrado = false;
    let vigente = true;
    let idFin: ReturnType<typeof setTimeout> | undefined;

    const limpiar = () =>
      document
        .querySelectorAll('.cita-destacada')
        .forEach((el) => el.classList.remove('cita-destacada'));

    const terminar = () => {
      vigente = false;
      observer.disconnect();
      limpiar();
    };

    const marcar = () => {
      const el = document.querySelector<HTMLElement>(selector);
      if (!el || el.classList.contains('cita-destacada')) return;

      el.classList.add('cita-destacada');

      if (encontrado) return;
      encontrado = true;
      traza('4. elemento encontrado');

      // Un frame de margen: el grid puede seguir ajustando altura y el
      // cálculo del centrado saldría desviado.
      requestAnimationFrame(() =>
        centrarEnScroller(el, objetivo.hora, calendarRef)
      );

      // El plazo se cuenta desde que aparece, no desde que monta el efecto.
      clearTimeout(idAbandono);
      idFin = setTimeout(terminar, DURACION_RESALTADO_MS);
    };

    const observer = new MutationObserver(() => {
      if (vigente) marcar();
    });

    // Red de seguridad: si la cita no llega a pintarse (vista de mes, o ha
    // dejado de estar en el rango), al menos se deja el grid a su hora.
    const idAbandono = setTimeout(() => {
      terminar();
      traza('4. NO apareció el elemento | marcados en DOM:',
        document.querySelectorAll('.fc-event[data-id-cita]').length);
      calendarRef.current?.getApi().scrollToTime(objetivo.hora);
    }, ESPERA_MAXIMA_MS);

    observer.observe(document.body, { childList: true, subtree: true });
    marcar();

    return () => {
      vigente = false;
      observer.disconnect();
      clearTimeout(idAbandono);
      if (idFin) clearTimeout(idFin);
      limpiar();
    };
  }, [objetivo, calendarRef]);

  // Fecha para el `initialDate` de FullCalendar. Con ella el calendario se
  // monta ya en el día de la cita y la primera petición de citas cubre ese
  // rango. Sin ella arrancaba en hoy, pedía la semana de hoy, el sondeo de
  // arriba hacía gotoDate y se volvía a pedir todo: dos cargas seguidas y
  // el calendario vacío entre medias, que es lo que se veía como "pillado".
  // El sondeo se queda como red de seguridad; con initialDate confirma a la
  // primera y no toca nada.
  return { fechaInicial: fechaParam ?? undefined };
}